import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { env } from '../../config/env';
import '../../utils/leafletSetup';


const userIcon = L.divIcon({
  className: '',
  html: '<div style="height:16px;width:16px;border-radius:9999px;border:2px solid #fff;background:#3b82f6;box-shadow:0 2px 8px rgba(0,0,0,.25)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const salonIcon = (active) =>
  L.divIcon({
    className: '',
    html: `<div style="display:flex;height:36px;width:36px;align-items:center;justify-content:center;border-radius:9999px;border:2px solid #fff;background:${
      active ? '#d946ef' : '#9333ea'
    };color:#fff;font-size:14px;box-shadow:0 4px 10px rgba(0,0,0,.25)">✂</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -34],
  });

const MapClickHandler = ({ onCenterChange }) => {
  useMapEvents({
    click(e) {
      onCenterChange?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

const MapFlyTo = ({ center, zoom = 14 }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([center.lat, center.lng], zoom, { duration: 0.8 });
  }, [center.lat, center.lng, zoom, map]);
  return null;
};

const FitBounds = ({ salons, center }) => {
  const map = useMap();
  useEffect(() => {
    const points = salons
      .filter((s) => s.latitude && s.longitude)
      .map((s) => [s.latitude, s.longitude]);
    if (points.length > 1) {
      map.fitBounds([...points, [center.lat, center.lng]], { padding: [48, 48], maxZoom: 14 });
    }
  }, [salons, center, map]);
  return null;
};

const SalonPopupCard = ({ salon, t }) => (
  <div className="w-56">
    <div className="h-24 w-full overflow-hidden rounded-lg bg-zinc-100">
      {salon.coverImage || salon.logo ? (
        <img src={salon.coverImage || salon.logo} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-zinc-500">{t('common.noImage')}</div>
      )}
    </div>
    <p className="mt-2 font-semibold text-zinc-900">{salon.businessName}</p>
    <p className="mt-1 text-xs font-medium text-zinc-600">{t(`salon.audience.${salon.audienceType || 'UNISEX'}`)}</p>
    {salon.phone && <p className="text-xs text-zinc-600">{salon.phone}</p>}
    <p className="mt-1 text-xs text-amber-600">
      ★ {salon.averageRating || '—'} · {t('explore.reviewsCount', { count: salon.totalReviews || 0 })}
    </p>
    <p className="mt-1 text-xs text-zinc-500">
      {t('explore.distanceAway', { km: salon.distanceKm })}
    </p>
    <Link
      to={`/explore/${salon._id}`}
      className="mt-2 inline-block text-sm font-medium text-glow-600 hover:underline"
    >
      {t('explore.viewProfileLink')}
    </Link>
  </div>
);

const ExploreSalonMap = ({
  center,
  radiusKm = 10,
  salons = [],
  selectedSalonId,
  onSelectSalon,
  onCenterChange,
}) => {
  const { t } = useTranslation();
  const mapCenter = useMemo(() => [center.lat, center.lng], [center.lat, center.lng]);

  return (
    <div className="explore-salon-map relative h-[min(70vh,640px)] overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800">
      <MapContainer center={mapCenter} zoom={13} className="h-full w-full" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={env.mapTileUrl}
        />
        <MapClickHandler onCenterChange={onCenterChange} />
        <MapFlyTo center={center} />
        <FitBounds salons={salons} center={center} />
        <Circle
          center={mapCenter}
          radius={radiusKm * 1000}
          pathOptions={{ color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.08, weight: 2 }}
        />
        <Marker position={mapCenter} icon={userIcon}>
          <Popup>{t('explore.searchCenter')}</Popup>
        </Marker>
        {salons.map((salon) => (
          <Marker
            key={salon._id}
            position={[salon.latitude, salon.longitude]}
            icon={salonIcon(salon._id === selectedSalonId)}
            eventHandlers={{
              click: () => onSelectSalon?.(salon._id),
            }}
          >
            <Popup>
              <SalonPopupCard salon={salon} t={t} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="pointer-events-none absolute left-3 top-3 rounded-lg bg-white/95 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow dark:bg-zinc-900/95 dark:text-zinc-200">
        {t('explore.radiusLabel', { km: radiusKm })}
      </div>
      <p className="pointer-events-none absolute bottom-3 left-3 right-3 rounded-lg bg-white/90 px-3 py-2 text-center text-xs text-zinc-600 shadow dark:bg-zinc-900/90 dark:text-zinc-400">
        {t('explore.mapClickHint')}
      </p>
    </div>
  );
};

export default ExploreSalonMap;
